/**
 * User Activity Service
 * Handles user activity tracking API calls (login history, API usage, account changes)
 */

import { API_ENDPOINTS } from '../config/api';
import { makeAuthenticatedRequest, createApiError } from './apiService';

export interface LoginAttempt {
  id: string;
  email: string;
  success: boolean;
  failureReason?: string;
  ipAddress?: string;
  userAgent?: string;
  attemptedAt: string;
}

export interface AccountChange {
  id: string;
  changeType: string;
  oldValue?: string;
  newValue?: string;
  description: string;
  changedByName: string;
  changedAt: string;
}

export interface EndpointUsage {
  endpoint: string;
  method: string;
  requestCount: number;
}

export interface DailyUsage {
  date: string;
  requestCount: number;
}

export interface ApiUsageResponse {
  userId: string;
  email: string;
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  endpointBreakdown: EndpointUsage[];
  dailyUsage: DailyUsage[];
  periodStart: string;
  periodEnd: string;
}

export interface ActivityBreakdownItem {
  service: string;
  count: number;
  billableCount: number;
}

export interface RecentRequestItem {
  service: string;
  detail?: string;
  billable: boolean;
  occurredAt: string;
}

export interface UserActivitySummary {
  userId: string;
  email: string;
  fullName: string;
  createdAt: string;
  lastLoginAt?: string;
  totalLogins: number;
  failedLoginsLast30Days: number;
  apiRequestsLast30Days: number;
  recentAccountChanges: number;
  activeApiKeys: number;
  isActive: boolean;
  mfaEnabled: boolean;
  recentLogins: LoginAttempt[];
  recentChanges: AccountChange[];
  activityBreakdown: ActivityBreakdownItem[];
  recentRequests: RecentRequestItem[];
}

/**
 * Get login history for a user
 */
export const getLoginHistory = async (
  userId: string,
  days: number = 30,
  page: number = 1,
  pageSize: number = 20
): Promise<LoginAttempt[]> => {
  const url = `${API_ENDPOINTS.USER_ACTIVITY.LOGINS(userId)}?days=${days}&page=${page}&pageSize=${pageSize}`;
  const response = await makeAuthenticatedRequest(url);

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Get account changes for a user
 */
export const getAccountChanges = async (
  userId: string,
  days: number = 90,
  page: number = 1,
  pageSize: number = 20
): Promise<AccountChange[]> => {
  const url = `${API_ENDPOINTS.USER_ACTIVITY.CHANGES(userId)}?days=${days}&page=${page}&pageSize=${pageSize}`;
  const response = await makeAuthenticatedRequest(url);

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Get API usage statistics for a user
 */
export const getApiUsage = async (userId: string, days: number = 30): Promise<ApiUsageResponse> => {
  const url = `${API_ENDPOINTS.USER_ACTIVITY.API_USAGE(userId)}?days=${days}`;
  const response = await makeAuthenticatedRequest(url);

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

export interface AdminUserRow {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  isSystemAdmin: boolean;
  lastLoginAt?: string;
  createdAt: string;
  clientId?: string;
  clientName?: string;
  apiRequestsLast30Days: number;
}

/**
 * SystemAdmin: fetch all users across all clients with last-login and API usage.
 */
export const getAllUsersAdmin = async (startDate?: string, endDate?: string): Promise<AdminUserRow[]> => {
  let url = API_ENDPOINTS.USER_ACTIVITY.ADMIN_ALL;
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (params.toString()) url += `?${params.toString()}`;
  const response = await makeAuthenticatedRequest(url);
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

/**
 * Get comprehensive activity summary for a user
 */
export const getActivitySummary = async (userId: string, startDate?: string, endDate?: string): Promise<UserActivitySummary> => {
  let url = API_ENDPOINTS.USER_ACTIVITY.SUMMARY(userId);
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (params.toString()) url += `?${params.toString()}`;
  const response = await makeAuthenticatedRequest(url);

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};
